import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Shield, ChevronRight, X, Loader2, ArrowRightLeft, Calendar } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { isNative, isIOS } from '../services/native';

const BILLING_INTERVALS = [
  { id: 'monthly', label: 'Monthly', period: '/month' },
  { id: 'quarterly', label: 'Quarterly', period: '/3 months', discount: 'Save 17%' },
  { id: 'yearly', label: 'Yearly', period: '/year', discount: 'Save 37%' }
];

const FEATURES = [
  'Unlimited transactions & accounts',
  'Budget tracking & alerts',
  'Receivables & debts with reminders',
  'Recurring transactions',
  'Statistics & insights',
  'Data export (PDF, Excel)',
];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const {
    subscription, plans, loading: subscriptionLoading, isPremium, isTrialing, trialDaysLeft,
    applyCoupon, removeCoupon, couponCode, setCouponCode, appliedCoupon,
    createCheckout, verifyPayment, getFinalPrice, refreshSubscription,
    isActive, error: subscriptionError, clearError, hidePaymentUI, userCurrency,
    switchPlan
  } = useSubscription();

  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState('yearly');
  const [switchLoading, setSwitchLoading] = useState(false);
  const paymentInProgressRef = useRef(false);

  // iOS native: show Apple IAP via RevenueCat
  const [appleOffering, setAppleOffering] = useState(null);
  const [appleLoading, setAppleLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);

  useEffect(() => {
    if (isIOS) {
      import('../services/appleIAP').then(async ({ getAppleOfferings }) => {
        const offering = await getAppleOfferings();
        setAppleOffering(offering);
      });
    }
  }, []);

  if (isIOS) {
    const handleApplePurchase = async (pkg) => {
      setAppleLoading(true);
      try {
        const { purchasePackage } = await import('../services/appleIAP');
        await purchasePackage(pkg);
        toast.success('Subscription activated!');
        refreshSubscription();
        navigate('/');
      } catch (err) {
        if (err.code !== 'USER_CANCELLED') {
          toast.error(err.message || 'Purchase failed. Please try again.');
        }
      } finally {
        setAppleLoading(false);
      }
    };

    const handleRestore = async () => {
      setRestoreLoading(true);
      try {
        const { restorePurchases } = await import('../services/appleIAP');
        await restorePurchases();
        toast.success('Purchases restored!');
        refreshSubscription();
        navigate('/');
      } catch (err) {
        toast.error('No active subscription found to restore.');
      } finally {
        setRestoreLoading(false);
      }
    };

    const packages = appleOffering?.availablePackages || [];
    const [selectedPkg, setSelectedPkg] = useState(null);

    // Auto-select yearly as default
    useEffect(() => {
      if (packages.length > 0 && !selectedPkg) {
        const yearly = packages.find(p => p.packageType === 'ANNUAL');
        setSelectedPkg(yearly || packages[0]);
      }
    }, [packages]);

    return (
      <div className="space-y-4 max-w-md mx-auto">
        <div className="text-center">
          <span className="text-3xl">👑</span>
          <h1 className="text-xl font-bold text-surface-900 mt-2">Hisab Premium</h1>
          <p className="text-sm text-surface-500 mt-1">Unlock all features</p>
        </div>

        <div className="space-y-3">
          {packages.length > 0 ? packages.map((pkg) => {
            const product = pkg.product;
            const isYearly = pkg.packageType === 'ANNUAL';
            const isSelected = selectedPkg?.identifier === pkg.identifier;
            return (
              <button
                key={pkg.identifier}
                onClick={() => setSelectedPkg(pkg)}
                disabled={isActive && !isTrialing}
                className={`w-full rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.98] disabled:opacity-50 relative ${
                  isSelected ? 'border-primary-500 bg-primary-50' : 'border-surface-200 bg-white'
                }`}
              >
                {isYearly && (
                  <span className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-bold text-white rounded-full" style={{ backgroundColor: 'var(--accent-bg)' }}>
                    Best Value
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-primary-500' : 'border-surface-300'}`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                    </div>
                    <div>
                      <p className="font-semibold text-surface-900">{product.title}</p>
                      <p className="text-xs text-surface-500">{product.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-surface-900">{product.priceString}</p>
                  </div>
                </div>
              </button>
            );
          }) : (
            <div className="text-center py-8">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-surface-500">Loading plans...</p>
            </div>
          )}
        </div>

        {/* Subscribe button — show for trial, expired, cancelled users */}
        {packages.length > 0 && !(isActive && !isTrialing) && (
          <button
            onClick={() => selectedPkg && handleApplePurchase(selectedPkg)}
            disabled={appleLoading || !selectedPkg}
            className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            {appleLoading ? 'Processing...' : 'Subscribe Now'}
          </button>
        )}

        {/* Restore — for returning users */}
        <button
          onClick={handleRestore}
          disabled={restoreLoading}
          className="w-full py-2 text-xs font-medium text-surface-400 hover:text-surface-600 transition-colors"
        >
          {restoreLoading ? 'Restoring...' : 'Already subscribed? Tap to restore'}
        </button>

        {isActive && !isTrialing && (
          <div className="text-center">
            <p className="text-sm text-green-600 font-medium">You have an active subscription</p>
          </div>
        )}

        {/* Required by Apple Guideline 3.1.2(c) */}
        <div className="flex items-center justify-center gap-3 pt-2">
          <a href="https://hisabjourney.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[11px] text-surface-400 underline">Privacy Policy</a>
          <span className="text-surface-300">|</span>
          <a href="https://hisabjourney.com/terms" target="_blank" rel="noopener noreferrer" className="text-[11px] text-surface-400 underline">Terms of Use</a>
        </div>
      </div>
    );
  }

  // For active paid users, default to their current billing interval
  useEffect(() => {
    if (isActive && !isTrialing && subscription?.billingInterval) {
      setSelectedInterval(subscription.billingInterval);
    }
  }, [isActive, isTrialing, subscription?.billingInterval]);

  const intervalInfo = useMemo(() => {
    return BILLING_INTERVALS.find(i => i.id === selectedInterval);
  }, [selectedInterval]);

  // Whether user is an active paid subscriber (not trial)
  const isActivePaid = isActive && !isTrialing;

  // Scheduled plan change info
  const scheduledChange = subscription?.scheduledPlanChange?.billingInterval || null;

  useEffect(() => { clearError(); setCouponError(''); }, []);

  const checkPaymentOnResume = useCallback(async () => {
    if (!paymentInProgressRef.current) return;
    try { await refreshSubscription(); } catch {}
  }, [refreshSubscription]);

  useEffect(() => {
    if (!isNative) return;
    let appModule;
    const setupListener = async () => {
      const { App } = await import('@capacitor/app');
      appModule = App;
      appModule.addListener('appStateChange', ({ isActive }) => {
        if (isActive) checkPaymentOnResume();
      });
    };
    setupListener().catch(() => {});
    return () => { appModule?.removeAllListeners?.(); };
  }, [checkPaymentOnResume]);

  useEffect(() => {
    if (paymentInProgressRef.current && isActive) {
      paymentInProgressRef.current = false;
      setCheckoutLoading(false);
      navigate('/checkout/success');
    }
  }, [isActive, navigate]);

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponError('');
    setCouponLoading(true);
    try {
      await applyCoupon(couponInput.trim().toUpperCase(), selectedInterval);
      toast.success('Coupon applied!');
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    removeCoupon();
    setCouponInput('');
    setCouponError('');
  };

  const handleSubscribe = async () => {
    setCheckoutLoading(true);
    paymentInProgressRef.current = true;
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setCheckoutLoading(false);
        paymentInProgressRef.current = false;
        return;
      }
      const checkoutData = await createCheckout(appliedCoupon ? couponCode : null, selectedInterval);
      const options = {
        key: import.meta.env.VITE_RAZORPAY_KEY_ID,
        subscription_id: checkoutData.razorpaySubscriptionId,
        name: 'Hisab Journey',
        description: `${intervalInfo?.label || 'Monthly'} Subscription`,
        image: 'https://app.hisabjourney.com/logo.png',
        handler: async function (response) {
          try {
            await verifyPayment({
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySubscriptionId: response.razorpay_subscription_id,
              razorpaySignature: response.razorpay_signature
            });
            paymentInProgressRef.current = false;
            setCheckoutLoading(false);
            navigate('/checkout/success');
          } catch (err) {
            toast.error(err.message || 'Payment verification failed');
            setCheckoutLoading(false);
          }
        },
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#6366f1' },
        modal: {
          ondismiss: function () {
            paymentInProgressRef.current = false;
            setCheckoutLoading(false);
          }
        }
      };
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error(response.error.description || 'Payment failed');
        paymentInProgressRef.current = false;
        setCheckoutLoading(false);
      });
      razorpay.open();
    } catch (err) {
      toast.error(err.message || 'Failed to initiate checkout');
      paymentInProgressRef.current = false;
      setCheckoutLoading(false);
    }
  };

  const handleSwitchPlan = async (interval) => {
    setSwitchLoading(true);
    try {
      const result = await switchPlan(interval);
      toast.success(result.message);
      await refreshSubscription();
    } catch (err) {
      toast.error(err.message || 'Failed to switch plan');
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleCancelSwitch = async () => {
    // Cancel by switching back to current interval
    setSwitchLoading(true);
    try {
      const result = await switchPlan(subscription.billingInterval);
      toast.success(result.message || 'Plan change cancelled');
      await refreshSubscription();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel plan change');
    } finally {
      setSwitchLoading(false);
    }
  };

  if (subscriptionLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const trialText = trialDaysLeft === 0 ? 'Trial ends today' : trialDaysLeft === 1 ? '1 day left in trial' : `${trialDaysLeft} days left in trial`;
  const intervalLabels = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

  return (
    <div className="pb-24 xl:pb-8 max-w-lg mx-auto space-y-3">
      {/* Header */}
      <div className="text-center pt-2 pb-4">
        <span className="text-4xl block mb-3">👑</span>
        <h1 className="text-xl font-bold text-surface-900">
          {isActivePaid
            ? 'Your Plan'
            : isPremium
              ? 'Your Subscription'
              : hidePaymentUI
                ? 'Hisab Journey Premium'
                : 'Upgrade to Premium'}
        </h1>
        <p className="text-[13px] text-surface-400 mt-1">
          {isActivePaid
            ? 'Manage your billing plan'
            : isTrialing
              ? trialText
              : isPremium
                ? 'Full access to all features'
                : hidePaymentUI
                  ? 'All the powerful features below'
                  : 'Get unlimited access to all features'}
        </p>
      </div>

      {/* Trial/Active Status */}
      {isPremium && (
        <div
          className="flex items-center gap-3 p-3.5 rounded-2xl"
          style={isTrialing
            ? { background: '#fffbeb', border: '1px solid #fde68a' }
            : { background: '#f0fdf4', border: '1px solid #bbf7d0' }
          }
        >
          <span className="text-xl">{isTrialing ? '⏳' : '✅'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold" style={{ color: isTrialing ? '#92400e' : '#166534' }}>
              {isTrialing ? 'Trial Active' : 'Premium Active'}
            </p>
            <p className="text-[11px]" style={{ color: isTrialing ? '#b45309' : '#16a34a' }}>
              {isTrialing
                ? hidePaymentUI ? `${trialText}.` : `${trialText}. Subscribe to continue.`
                : `${intervalLabels[subscription?.billingInterval] || 'Monthly'} plan — renews ${subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : ''}`}
            </p>
          </div>
        </div>
      )}

      {/* Scheduled Plan Change Notice */}
      {scheduledChange && (
        <div className="flex items-center gap-3 p-3.5 rounded-2xl" style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <span className="text-xl">🔄</span>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold" style={{ color: '#1e40af' }}>
              Switching to {intervalLabels[scheduledChange]}
            </p>
            <p className="text-[11px]" style={{ color: '#3b82f6' }}>
              Takes effect after current period ends{subscription?.scheduledPlanChange?.effectiveAt ? ` on ${new Date(subscription.scheduledPlanChange.effectiveAt).toLocaleDateString()}` : ''}
            </p>
          </div>
          <button
            onClick={handleCancelSwitch}
            disabled={switchLoading}
            className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors"
            style={{ color: '#3b82f6' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Subscription History */}
      <button
        onClick={() => navigate('/subscription')}
        className="w-full flex items-center justify-between p-3 rounded-2xl bg-white border border-surface-200 transition-all active:bg-surface-50"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#f1f5f9' }}>
            <span className="text-sm">📋</span>
          </div>
          <span className="text-[13px] font-semibold text-surface-900">Subscription History</span>
        </div>
        <ChevronRight className="w-4 h-4 text-surface-400" />
      </button>

      {/* Active Paid User — Plan Switcher */}
      {isActivePaid && !hidePaymentUI && (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <div className="p-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="flex items-center gap-2 mb-3">
              <ArrowRightLeft className="w-4 h-4 text-primary-500" />
              <p className="text-[13px] font-semibold text-surface-900">Switch Plan</p>
            </div>
            <div className="space-y-2.5">
              {BILLING_INTERVALS.map((interval) => {
                const isCurrent = subscription?.billingInterval === interval.id;
                const isScheduled = scheduledChange === interval.id;
                const price = plans?.intervals?.[interval.id]?.[userCurrency || 'INR'];

                return (
                  <div
                    key={interval.id}
                    className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                      isCurrent
                        ? 'border-primary-300 bg-primary-50'
                        : isScheduled
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-surface-200 bg-surface-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-[13px] font-semibold ${isCurrent ? 'text-primary-700' : 'text-surface-900'}`}>
                          {interval.label}
                        </p>
                        {isCurrent && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary-500 text-white">
                            Current
                          </span>
                        )}
                        {isScheduled && !isCurrent && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500 text-white">
                            Scheduled
                          </span>
                        )}
                        {interval.discount && !isCurrent && (
                          <span className="text-[10px] font-semibold text-green-600">{interval.discount}</span>
                        )}
                      </div>
                      <p className="text-[12px] text-surface-500 mt-0.5">
                        {price?.formatted || '-'}{interval.period}
                      </p>
                    </div>
                    {!isCurrent && !isScheduled && (
                      <button
                        onClick={() => handleSwitchPlan(interval.id)}
                        disabled={switchLoading}
                        className="px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-50"
                        style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                      >
                        {switchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Switch'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-surface-400 mt-3 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Plan changes take effect after your current billing period ends
            </p>
          </div>
        </div>
      )}

      {/* iOS: Feature list only */}
      {hidePaymentUI && (!isPremium || isTrialing) && (
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#ede9fe' }}>
              <span className="text-lg">⚡</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-surface-900">Premium Features</p>
              <p className="text-[11px] text-surface-400">Everything included in your plan</p>
            </div>
          </div>
          <div className="mt-3 space-y-2.5">
            {FEATURES.map((feature) => (
              <div key={feature} className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#dcfce7' }}>
                  <Check className="w-3 h-3" style={{ color: '#16a34a' }} />
                </div>
                <span className="text-[13px] text-surface-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plan Cards (non-iOS, not active paid) — for trial users and expired/new users */}
      {!hidePaymentUI && (!isPremium || isTrialing) && (
        <div className="space-y-3">
          {BILLING_INTERVALS.map((interval) => {
            const price = plans?.intervals?.[interval.id]?.[userCurrency || 'INR'];
            const isSelected = selectedInterval === interval.id;
            const isYearly = interval.id === 'yearly';
            return (
              <button
                key={interval.id}
                onClick={() => setSelectedInterval(interval.id)}
                className={`w-full rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.98] relative ${
                  isSelected ? 'border-primary-500 bg-primary-50' : 'border-surface-200 bg-white'
                }`}
              >
                {isYearly && (
                  <span className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-bold text-white rounded-full" style={{ backgroundColor: 'var(--accent-bg)' }}>
                    Best Value
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-primary-500' : 'border-surface-300'}`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                    </div>
                    <div>
                      <p className="font-semibold text-surface-900">{interval.label}</p>
                      <p className="text-xs text-surface-500">
                        {interval.discount || 'Billed ' + interval.label.toLowerCase()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {price?.formatted || '-'}
                    </p>
                    <p className="text-[10px] text-surface-400">{interval.period}</p>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Subscribe */}
          <button
            onClick={handleSubscribe}
            disabled={checkoutLoading}
            className="w-full py-3.5 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            {checkoutLoading ? (
              <><Loader2 className="w-5 h-5 animate-spin" />Processing...</>
            ) : (
              'Subscribe Now'
            )}
          </button>
          <p className="text-center text-[11px] text-surface-400">Cancel anytime. No questions asked.</p>
        </div>
      )}

      {/* Manage Subscription (active paid users) */}
      {isPremium && !isTrialing && (
        <button
          onClick={() => navigate('/subscription')}
          className="w-full flex items-center justify-between p-3 rounded-2xl bg-white border border-surface-200 transition-all active:bg-surface-50"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#ede9fe' }}>
              <Shield className="w-4 h-4" style={{ color: '#7c3aed' }} />
            </div>
            <span className="text-[13px] font-semibold text-surface-900">Manage Subscription</span>
          </div>
          <ChevronRight className="w-4 h-4 text-surface-400" />
        </button>
      )}

      {/* Security note */}
      {!hidePaymentUI && (
        <div className="flex items-center justify-center gap-1.5 pt-2 pb-4">
          <Shield className="w-3.5 h-3.5 text-surface-400" />
          <p className="text-[11px] text-surface-400">Secure payment via Razorpay</p>
        </div>
      )}
    </div>
  );
}
